import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Phone } from "lucide-react";
import { BRAND, ASSETS, buildWhatsAppLink } from "../lib/brand";
import { fetchNavigation } from "../lib/api";

const DEFAULT_LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/about", label: "About", id: "about" },
  { to: "/products", label: "Products", id: "products" },
  { to: "/gifting", label: "Gifting", id: "gifting" },
  { to: "/gallery", label: "Gallery", id: "gallery" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [links, setLinks] = useState(DEFAULT_LINKS);
  const location = useLocation();

  useEffect(() => {
    fetchNavigation()
      .then((items) => {
        if (Array.isArray(items) && items.length > 0) setLinks(items);
      })
      .catch(() => {
        /* keep defaults */
      });
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // eslint-disable-next-line
  useEffect(() => {
    setOpen((prev) => (prev ? false : prev));
  }, [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-md bg-[#F9F4EC]/90 shadow-[0_2px_24px_rgba(59,30,26,0.08)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src={ASSETS.logo}
            alt="Qamar Al Zahran logo"
            className="h-14 w-14 object-contain rounded-lg bg-brand-ivory transition-transform group-hover:scale-105 shadow-sm"
            loading="eager"
          />
          <div className="hidden md:flex flex-col leading-tight">
            <span className="font-serif-display text-xl text-brand-brown font-semibold">
              {BRAND.name}
            </span>
            <span className="text-[10px] tracking-[0.22em] uppercase text-brand-gold">
              QRZ · Roastery
            </span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {links.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-${l.id}-link`}
              end={l.to === "/"}
              className={({ isActive }) =>
                `gold-underline text-sm tracking-wide font-medium transition-colors ${
                  isActive ? "text-brand-gold" : "text-brand-brown hover:text-brand-gold"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={BRAND.instagramUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-instagram-link"
            className="text-brand-brown hover:text-brand-gold transition-colors p-2"
            aria-label="Instagram"
          >
            <Instagram className="h-5 w-5" />
          </a>
          <a
            href={buildWhatsAppLink("Hello Qamar Al Zahran! I'd like to place an order.")}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-whatsapp-cta"
            className="inline-flex items-center gap-2 bg-brand-brown text-brand-ivory px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#5A2F2A] transition-all hover:shadow-lg"
          >
            <Phone className="h-4 w-4 text-brand-gold" />
            Order Now
          </a>
        </div>

        {/* Mobile burger */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((s) => !s)}
          className="lg:hidden p-2 text-brand-brown"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#F9F4EC] border-t border-[#E8D6C3] animate-fade-in"
        >
          <div className="px-6 py-6 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                data-testid={`mobile-nav-${l.id}-link`}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `py-3 text-base font-medium transition-colors ${
                    isActive ? "text-brand-gold" : "text-brand-brown"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={buildWhatsAppLink("Hello Qamar Al Zahran! I'd like to place an order.")}
              target="_blank"
              rel="noreferrer"
              data-testid="mobile-whatsapp-cta"
              className="mt-3 inline-flex items-center justify-center gap-2 bg-brand-brown text-brand-ivory px-5 py-3 rounded-full text-sm font-medium"
            >
              <Phone className="h-4 w-4 text-brand-gold" />
              Order on WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
